// src/components/layout/Navbar/Navbar.jsx

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import event from "../../assets/images/logo.svg";
import { useSelector, useDispatch } from "react-redux";
import { getProfile } from '../../redux/auth/action/profileActions';
import { setFilters } from '../../redux/auth/action/filtersActions';
import { AnimatePresence } from "framer-motion";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SearchForm from "./Search/SearchForm";
import CategoryNav from "./CategoryNav";
import AuthLinks from "./MenuItems/AuthLinks";
import ProfileLinks from "./MenuItems/ProfileLinks";
import MenuNav from "./MenuItems/MenuNav";
import MobileMenu from "./MenuItems/MobileMenu";
import apiClient from '../../apiClient/apiClient'; 

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCategoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { profile } = useSelector((state) => state.profile);
  const currentCategoryFilter = useSelector((state) => state.filters.category);
  const firstName = profile?.full_name?.split(" ")[0];
  const token = localStorage.getItem("access") || sessionStorage.getItem("access");

  // Separate useEffect for profile fetching
  useEffect(() => {
    if (token) {
      dispatch(getProfile());
    }
    setLoading(false);
  }, [dispatch, token]);

  // Separate useEffect for categories fetching with proper loading state
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await apiClient.get("/category/");
        // Handle the nested structure
        const categoryData = response.data.results || response.data || [];
        setCategories(categoryData);
        setCategoriesError(null);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategoriesError("Failed to load categories");
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    
    fetchCategories();
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <nav className="relative bg-white shadow-md">
      <div className="flex max-w-[1200px] mx-auto px-4 justify-between items-center py-4 min-h-[80px] lg:min-h-[120px]">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0 mr-2 lg:mr-4">
          <img src={event} className="w-16 h-12 sm:w-20 sm:h-16 lg:w-30 lg:h-20 rounded-md" alt="Event" />
        </Link>

        {/* Desktop Middle section */}
        <div className="hidden lg:flex flex-grow flex-col items-center w-full">
          <div className="flex items-center justify-center w-full max-w-2xl mb-2">
            <SearchForm />
          </div>
          {isCategoriesLoading ? (
            <div className="py-2 text-sm text-gray-500">Loading categories...</div>
          ) : categoriesError ? (
            <div className="py-2 text-red-500 text-sm">Error: {categoriesError}</div>
          ) : (
            <CategoryNav categories={categories} />
          )}
        </div>

        {/* Tablet Middle section */}
        <div className="hidden md:flex lg:hidden flex-grow flex-col items-center w-full mx-4">
          <div className="flex items-center justify-center w-full max-w-xl mb-2">
            <SearchForm />
          </div>
          {/* Show categories on tablet view */}
          {isCategoriesLoading ? (
            <div className="py-2 text-sm text-gray-500">Loading categories...</div>
          ) : categoriesError ? (
            <div className="py-2 text-red-500 text-sm">Error: {categoriesError}</div>
          ) : (
            <div className="flex flex-wrap justify-center gap-2">
              {categories.slice(0, 4).map((category) => {
                const isActive = currentCategoryFilter === category.category_name;
                return (
                  <button
                    key={category.category_id}
                    onClick={() => {
                      const newCategory = currentCategoryFilter === category.category_name ? "" : category.category_name;
                      dispatch(setFilters({ category: newCategory }));
                      if (location.pathname !== '/') {
                        navigate('/');
                      }
                    }}
                    className={`px-3 py-1 text-xs border rounded-full transition-all duration-200 font-medium ${
                      isActive
                        ? 'bg-sunday_secondary text-white border-sunday_secondary shadow-md scale-105'
                        : 'bg-white text-sunday_info border-sunday_secondary hover:bg-sunday_primary hover:text-white hover:border-sunday_primary hover:scale-105'
                    }`}
                    title={`Filter by ${category.category_name}`}
                  >
                    {category.category_name}
                  </button>
                );
              })}
              {categories.length > 4 && (
                <button 
                  onClick={toggleMenu}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-600 border border-gray-300 rounded-full hover:bg-gray-200 transition-colors duration-200"
                >
                  +{categories.length - 4} more
                </button>
              )}
            </div>
          )}
        </div>

        {/* Mobile Search form - Clean and simple */}
        <div className="flex-grow mx-2 md:hidden">
          <SearchForm />
        </div>

        {/* Mobile Menu Button */}
        <MenuNav isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} />

        {/* Desktop Auth/Profile Links */}
        <div className="hidden md:flex items-center gap-4 lg:gap-6 flex-shrink-0">
          {token
            ? <ProfileLinks loading={loading} firstName={firstName} />
            : <AuthLinks />}
        </div>
      </div>

      {/* Mobile menu - Professional slide-out navigation */}
      <AnimatePresence>
        {isMenuOpen && 
          <MobileMenu 
            categories={categories} 
            loading={loading} 
            firstName={firstName}
            isMenuOpen={isMenuOpen}
            toggleMenu={toggleMenu}
          />
        }
      </AnimatePresence>

      {/* Toast notifications */}
      <ToastContainer position="top-right" autoClose={5000} />
    </nav>
  );
};

export default Navbar;
